import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const CONFIDENCE_STYLE = {
  HIGH: "text-emerald-400 border-emerald-400/30",
  MODERATE: "text-amber-400 border-amber-400/30",
  EXPLORATORY: "text-hussar-slate border-white/15",
};

function renderBody(body) {
  return body.split(/\n\n+/).map((block, i) => {
    const t = block.trim();
    if (!t) return null;
    if (t.startsWith("## ")) return <h2 key={i}>{t.slice(3)}</h2>;
    if (t.startsWith("### ")) return <h3 key={i}>{t.slice(4)}</h3>;
    if (t.startsWith("> ")) return <blockquote key={i}>{t.slice(2)}</blockquote>;
    const lines = t.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1 && lines.every((l) => l.startsWith("- "))) {
      return <ul key={i}>{lines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}</ul>;
    }
    return <p key={i}>{t}</p>;
  });
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        document.title = `${r.data.title} | Hussar Treasury Weekly Intelligence`;
        const head = document.head;
        const upsert = (attr, key, content) => {
          let el = head.querySelector(`meta[${attr}="${key}"]`);
          if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); head.appendChild(el); }
          el.setAttribute("content", content);
        };
        upsert("name", "description", r.data.dek || "Sanitized, owner-approved Hussar Lab research.");
        upsert("property", "og:title", r.data.title);
        upsert("property", "og:description", r.data.dek || "");
        upsert("property", "og:type", "article");
        upsert("property", "og:url", r.data.canonical_url);
        upsert("property", "og:image", `${process.env.REACT_APP_BACKEND_URL}/api/og/${r.data.slug}.png`);
        upsert("name", "twitter:card", "summary_large_image");
        upsert("name", "twitter:image", `${process.env.REACT_APP_BACKEND_URL}/api/og/${r.data.slug}.png`);
        let link = head.querySelector('link[rel="canonical"]');
        if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); head.appendChild(link); }
        link.setAttribute("href", r.data.canonical_url);
        head.querySelectorAll('script[data-seo="article"]').forEach((s) => s.remove());
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo", "article");
        script.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: r.data.title,
          description: r.data.dek,
          datePublished: r.data.published_at,
          dateModified: r.data.updated_at,
          author: { "@type": "Organization", name: "Hussar Lab" },
          publisher: { "@type": "Organization", name: "Hussar Treasury" },
          mainEntityOfPage: r.data.canonical_url,
          version: String(r.data.version),
        });
        head.appendChild(script);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-48 pb-40 px-6 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-4xl text-hussar-ink mb-6">This edition is not on the public record.</h1>
        <Link to="/intelligence" className="btn-ghost">Back to the Archive</Link>
      </div>
    );
  }

  if (!article) {
    return <div className="pt-48 pb-40 text-center text-hussar-muted font-mono text-xs uppercase tracking-widest">Retrieving edition…</div>;
  }

  const meta = [
    ["Publication Date", article.published_at ? new Date(article.published_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "—"],
    ["Research Period", article.research_period || "—"],
    ["Evidence Cutoff", article.evidence_cutoff || "—"],
    ["Article Version", `v${article.version}`],
    ["Source Report", article.learning_report_id || "—"],
    ["Category", article.category],
  ];

  return (
    <div data-testid="article-detail-page" className="pt-40 px-6 sm:px-10 lg:px-16 max-w-[1400px] mx-auto pb-28">
      <Reveal>
        <Link to="/intelligence" data-testid="back-to-archive-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-hussar-muted hover:text-hussar-gold transition-colors mb-12">
          <ArrowLeft size={14} /> Weekly Intelligence Archive
        </Link>
      </Reveal>

      {article.status === "UNPUBLISHED" && (
        <div data-testid="withdrawn-banner" className="border border-red-400/30 bg-red-400/5 px-6 py-4 mb-10 text-red-300 text-sm font-light">
          This edition has been withdrawn from active publication. It remains accessible as part of the permanent
          historical record and must not be cited as current research.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        {/* Metadata rail */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-32" data-testid="article-metadata-rail">
            <div className={`inline-block font-mono text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 mb-8 ${CONFIDENCE_STYLE[article.confidence_label] || CONFIDENCE_STYLE.EXPLORATORY}`} data-testid="article-confidence-label">
              {article.confidence_label} Confidence
            </div>
            <dl className="space-y-5">
              {meta.map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-muted mb-1">{k}</dt>
                  <dd className="text-sm text-hussar-ink font-light" data-testid={`article-meta-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
                </div>
              ))}
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-muted mb-1">Canonical URL</dt>
                <dd className="text-xs text-hussar-gold/80 font-mono break-all flex items-start gap-2" data-testid="article-canonical-url">
                  <Link2 size={12} className="shrink-0 mt-0.5" /> {article.canonical_url}
                </dd>
              </div>
              {(article.source_report_hash || article.article_hash) && (
                <div data-testid="provenance-block">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-muted mb-1">Provenance</dt>
                  <dd className="space-y-3">
                    {article.source_report_hash && (
                      <div>
                        <div className="text-[10px] font-mono text-hussar-muted">SOURCE REPORT SHA-256</div>
                        <div className="text-[10px] font-mono text-hussar-ink/70 break-all" data-testid="source-report-hash">{article.source_report_hash}</div>
                      </div>
                    )}
                    {article.article_hash && (
                      <div>
                        <div className="text-[10px] font-mono text-hussar-muted">PUBLIC EDITION SHA-256 · v{article.version}</div>
                        <div className="text-[10px] font-mono text-hussar-ink/70 break-all" data-testid="article-hash">{article.article_hash}</div>
                      </div>
                    )}
                    <p className="text-[10px] text-hussar-muted font-light leading-relaxed">
                      These hashes prove this public edition corresponds to the sanitized source report that existed
                      at publication time — without exposing the report itself.
                    </p>
                  </dd>
                </div>
              )}
            </dl>

            {article.versions?.length > 0 && (
              <div className="mt-10 border-t border-white/5 pt-8" data-testid="version-history">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-muted mb-4">Version History</div>
                {[...article.versions].reverse().map((v) => (
                  <div key={v.version} className="mb-4">
                    <div className="text-xs text-hussar-gold font-mono">v{v.version} · {v.published_at ? new Date(v.published_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}</div>
                    {v.change_note && <div className="text-xs text-hussar-muted font-light mt-1">{v.change_note}</div>}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-10 border border-hussar-gold/20 bg-hussar-card p-5" data-testid="methodology-box">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={14} className="text-hussar-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-hussar-gold">Methodology & Disclaimer</span>
              </div>
              <p className="text-xs text-hussar-muted font-light leading-relaxed">
                This edition is a sanitized extract of a private Hussar Lab Learning Report, published after explicit
                owner approval. It reflects evidence available up to the stated cutoff. It is not investment advice,
                an offer, or a solicitation. Conclusions are preserved permanently and corrected only by versioned addition.
              </p>
            </div>
          </div>
        </aside>

        {/* Article body */}
        <article className="lg:col-span-8 lg:col-start-5 order-1 lg:order-2">
          <Reveal>
            <div className="metadata-label mb-6">{article.category}</div>
            <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-hussar-ink tracking-tight leading-[1.08]" data-testid="article-title">
              {article.title}
            </h1>
            {article.dek && (
              <p className="mt-8 font-serif italic text-lg sm:text-xl text-hussar-slate leading-relaxed" data-testid="article-dek">
                {article.dek}
              </p>
            )}
          </Reveal>
          <div className="gold-hairline my-12" />
          <Reveal>
            <div className="article-body" data-testid="article-body">
              {renderBody(article.body || "")}
            </div>
          </Reveal>
        </article>
      </div>
    </div>
  );
}
